import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { SkillGapAnalysis } from './types';

interface SkillGapResultsProps {
  analysis: SkillGapAnalysis;
  expandedSkills: Set<number>;
  toggleSkillExpanded: (i: number) => void;
  getPriorityColor: (p: string) => string;
  getETypeColor: (e: string) => string;
}

export const SkillGapResults = ({
  analysis,
  expandedSkills,
  toggleSkillExpanded,
  getPriorityColor,
  getETypeColor,
}: SkillGapResultsProps) => (
  <div className="space-y-4">
    <p className="text-sm text-muted-foreground">{analysis.summary}</p>

    <div className="space-y-3">
      {analysis.skill_gaps?.map((gap, i) => (
        <Collapsible key={i} open={expandedSkills.has(i)} onOpenChange={() => toggleSkillExpanded(i)}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-3">
                <span className="font-medium">{gap.skill}</span>
                <Badge variant="outline" className={getPriorityColor(gap.importance)}>
                  {gap.importance}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{gap.current_level} → {gap.target_level}</span>
                {expandedSkills.has(i) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-3 space-y-3 border-x border-b rounded-b-lg bg-background/50">
              {gap.recommendations.education?.length > 0 && (
                <div>
                  <Badge className={getETypeColor('education')} variant="outline">Education</Badge>
                  <ul className="mt-2 text-sm space-y-1">
                    {gap.recommendations.education.map((rec, j) => (
                      <li key={j} className="text-muted-foreground">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              {gap.recommendations.exposure?.length > 0 && (
                <div>
                  <Badge className={getETypeColor('exposure')} variant="outline">Exposure</Badge>
                  <ul className="mt-2 text-sm space-y-1">
                    {gap.recommendations.exposure.map((rec, j) => (
                      <li key={j} className="text-muted-foreground">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              {gap.recommendations.experience?.length > 0 && (
                <div>
                  <Badge className={getETypeColor('experience')} variant="outline">Experience</Badge>
                  <ul className="mt-2 text-sm space-y-1">
                    {gap.recommendations.experience.map((rec, j) => (
                      <li key={j} className="text-muted-foreground">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>

    {analysis.quick_wins?.length > 0 && (
      <div className="p-3 bg-primary/5 rounded-lg">
        <h5 className="font-medium text-sm mb-2">Quick Wins</h5>
        <ul className="text-sm space-y-1">
          {analysis.quick_wins.map((win, i) => (
            <li key={i} className="text-muted-foreground">• {win}</li>
          ))}
        </ul>
      </div>
    )}

    {analysis.timeline_estimate && (
      <p className="text-sm text-muted-foreground">
        <strong>Estimated timeline:</strong> {analysis.timeline_estimate}
      </p>
    )}
  </div>
);
